import Link from 'next/link'
import { useRouter } from 'next/router'

import * as S from './styles'
import { ButtonIcon } from '../buttonIcon'
import { SubOptions } from '../header'
import { Image } from '../image'
import { LanguageSwitch } from '../languageSwitch'
import { Text } from '../text'
import {
  CloseIconBlack,
  DarkFacebookIcon,
  DarkInstagramIcon,
  DarkLinkedInIcon,
  WhatsAppIcon,
} from '@/assets/icons'
import { LogoNhBlack } from '@/assets/logos'
import { usePageContext } from '@/store/pageContext'

export type DrawerProps = {
  /**
   * boolean to check if Dark Mode is active
   * @example
   * ```tsx
   * <Drawer darkMode={true} />
   * ```
   * */
  darkMode: boolean

  /**
   * boolean to check if modal is open
   * @example
   * ```tsx
   * <Drawer open={open} />
   * ```
   * */
  open: boolean
  /**
   * Function to handle Open Drawer
   * @example
   * ```tsx
   * <Drawer setOpen={setOpen} />
   * ```
   * */
  setOpen: (boolean: boolean) => void
  /**
   *  Options of the dropdown
   *
   * @example
   * ```tsx
   * const options = [{text: 'Option 1', onClick: () => console.log('Option 1')}]
   *
   * <Drawer options={options} />
   * ```
   *
   * @default []
   *
   */
  options: {
    text: string
    onClick: () => void
    path?: string
    subOptions?: {
      text: string
      onClick: () => void
      path: string
    }[]
  }[]
  /**
   * boolean to check if is mobile/tablet
   * @example
   * ```tsx
   * <Drawer isBiggerMobile={isBiggerMobile} />
   * ```
   * */
  isBiggerMobile: boolean
  /**
   *  showSubOptions is show subOptions to according with item menu clicked
   *
   * @example
   * ```tsx
   *
   * <Drawer showSubOptions={showSubOptions} />
   * ```
   *
   * @default []
   *
   */
  showSubOptions: SubOptions
  /**
   * path for verify current route
   * @example
   * ```tsx
   * <Drawer path={router.pathname} />
   * ```
   * */
  path: string
}
export const Drawer: React.FC<DrawerProps> = ({
  open,
  options,
  isBiggerMobile,
  setOpen,
  showSubOptions,
  path,
}) => {
  const { notTranslated } = usePageContext()
  const { locale } = useRouter()
  const hideOptions = ['Blog']
  const optionsFiltereds =
    locale === 'en'
      ? options.filter((option) => !hideOptions.includes(option.text))
      : options

  return (
    <S.DrawerContainer tabIndex={-1}>
      {open && !isBiggerMobile && <div className="backdrop" />}
      <S.Drawer tabIndex={-1} className={open ? 'open' : ''}>
        <div className="logo">
          <Image
            src={LogoNhBlack}
            alt="Logo black da nata.house"
            className="logo-nata"
            defaultSize={{
              height: isBiggerMobile ? 24 : 58,
              width: isBiggerMobile ? 127 : 303,
            }}
          />
          <S.ButtonsContainer
            isBiggerMobile={isBiggerMobile}
            notTranslatePage={notTranslated}
          >
            {!notTranslated && (
              <LanguageSwitch isBiggerMobile={isBiggerMobile} />
            )}
            <ButtonIcon
              icon={CloseIconBlack}
              alt="Botão para fechar opções do menu"
              onClick={() => setOpen(false)}
              height={24}
              width={24}
            />
          </S.ButtonsContainer>
        </div>
        <div className="drawer-menu">
          {optionsFiltereds?.map((option, index) => (
            <div key={option.text} className="menu-options-container">
              <Text
                key={option.text}
                size={isBiggerMobile ? 'h5' : 'h4'}
                color={path === option?.path ? 'primary' : 'black'}
                onClick={option.onClick}
                className={`title ${
                  option?.subOptions?.length ? 'subOption' : ''
                }`}
              >
                {option.text}
              </Text>
              {showSubOptions.index === index && showSubOptions.show && (
                <div className="subOptions">
                  {option?.subOptions?.map((subOption) => (
                    <Text
                      key={subOption.text}
                      size={isBiggerMobile ? 'b3' : 'h5'}
                      color={path === option?.path ? 'primary' : 'black'}
                      onClick={subOption.onClick}
                      className="subTitle"
                    >
                      {subOption.text}
                    </Text>
                  ))}
                  <span className="border" />
                </div>
              )}
            </div>
          ))}
        </div>
        <div className="contact">
          <Text color="darkgray" size={isBiggerMobile ? 'b3' : 'b2'}>
            contato@natahouse.com
          </Text>
          <div className="social-media">
            <Link
              tabIndex={-1}
              target="_blank"
              href="https://api.whatsapp.com/send/?phone=5531984265166&text&type=phone_number&app_absent=0"
            >
              <Image
                src={WhatsAppIcon}
                alt="link pro whatsapp"
                defaultSize={{ height: 24, width: 24 }}
              />
            </Link>
            <Link
              tabIndex={-1}
              target="_blank"
              href="https://www.linkedin.com/company/natahouse/"
            >
              <Image
                src={DarkLinkedInIcon}
                alt="link pro linkedin"
                defaultSize={{ height: 24, width: 24 }}
              />
            </Link>
            <Link
              tabIndex={-1}
              target="_blank"
              href="https://www.instagram.com/nata.house/"
            >
              <Image
                src={DarkInstagramIcon}
                alt="link pro instagram"
                defaultSize={{ height: 24, width: 24 }}
              />
            </Link>
            <Link
              tabIndex={-1}
              target="_blank"
              href="https://www.facebook.com/nata.house.software/"
            >
              <Image
                src={DarkFacebookIcon}
                alt="link pro facebook"
                defaultSize={{ height: 24, width: 24 }}
              />
            </Link>
          </div>
        </div>
      </S.Drawer>
    </S.DrawerContainer>
  )
}
